<template>
  <DrawerDialog
    :title="title"
    :show-close-button="true"
    :showFooter="true"
    :custom-footer="true"
    :sm="true"
    data-test="put-on-hold-drawer"
    @closeEvent="closeModal"
  >
    <template #modal-content>
      <div class="mb-4">
        <label class="block text-sm font-medium text-ink-soft mb-2">Reasoning</label>
        <textarea
          v-model="reasoning"
          rows="4"
          class="w-full border border-line rounded-md p-2"
          placeholder="Enter reason why this order is being put on hold"
        />
        <p v-if="error" class="text-sm text-red-600 mt-2">{{ error }}</p>
      </div>
    </template>
    <template #footer>
      <button type="button" class="dash-btn" @click="closeModal" :disabled="saving">Cancel</button>
      <button type="button" class="dash-btn dash-btn--primary" @click="submit" :disabled="saving">
        <span
          v-if="saving"
          class="inline-block w-4 h-4 border-2 border-white rounded-full border-r-transparent animate-spin mr-2"
        ></span>
        {{ props.mode === 'resume' ? 'Resume Order' : 'Put On Hold' }}
      </button>
    </template>
  </DrawerDialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import DrawerDialog from '@/components/Shared/DrawerDialog.vue';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { showToast } from '@/composables/swal';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';

const props = defineProps<{
  orderItemId: number | string;
  mode?: 'put' | 'resume';
  title?: string;
}>();

const emits = defineEmits<{
  (e: 'close'): void;
  (e: 'success', data?: any): void;
}>();

// Local state
const reasoning = ref('');
const saving = ref(false);
const error = ref<string | null>(null);

const defaultTitle = computed(() =>
  props.mode === 'resume' ? 'Resume Order' : 'Put Order On Hold'
);
const title = computed(() => props.title ?? defaultTitle.value);

watch(
  () => props.mode,
  () => {
    // clear previous state when mode changes
    reasoning.value = '';
    error.value = null;
  }
);

const closeModal = () => {
  reasoning.value = '';
  error.value = null;
  emits('close');
};

const submit = async () => {
  error.value = null;
  // Require reasoning for both put and resume per request
  if (props.mode !== 'resume' && (!reasoning.value || !reasoning.value.toString().trim())) {
    error.value = 'Reasoning is required';
    return;
  }

  saving.value = true;
  try {
    const on_hold_flag = props.mode === 'resume' ? false : true;
    const payload = {
      on_hold: on_hold_flag,
      on_hold_reason: reasoning.value.toString().trim(),
      order_item_id: props.orderItemId,
    };
    const response = await http.post(`order/toggle-onhold-status/${props.orderItemId}`, payload);
    const resData = response.data;
    showToast(on_hold_flag ? 'Order put on hold.' : 'Order resumed.', 'success');
    emits('success', resData);
    closeModal();
  } catch (err) {
    handleResponseException(err);
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
/* minimal styling to match existing modals */
</style>
